import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ServiceRequestCard from "../../../Component/Workshop/QuickServices/ServiceRequestCard";
import QSLiveTrackingPanel from "../../../Component/Workshop/QuickServices/QSLiveTrackingPanel.jsx";
import { useWebSocket } from "../../../context/WebSocketContext.jsx";
import CreateLegalCaseModal from "./Legal/CreateLegalCaseModal.jsx";
import CreatecraneServiceModal from "./CreatecraneServiceModal.jsx";
import { useQuickServiceRequests, useQuickServiceStats, QS_KEYS,} from "../../../hooks/useCraneQueries.js";
import { useLegalCases, useLegalCaseStats, LEGAL_KEYS,} from "../../../hooks/useLegalQueries.js";
import {CraneIcon, TruckIcon, GavelIcon, ToolsIcon, PlusIcon, SearchIcon, RefreshIcon, AlertIcon2, CheckIcon2, ClockIcon, InboxIcon } from "../../../Component/Workshop/UI/icons.jsx";
import "./QuickServicesPage.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "crane", label: "Crane", Icon: CraneIcon },
  { key: "recovery", label: "Recovery van", Icon: TruckIcon },
  { key: "advocate", label: "Legal", Icon: GavelIcon },
  // { key: "mechanic", label: "Mechanic",     Icon: ToolsIcon },
];

const TRACKABLE_SERVICES = new Set(["crane", "recovery"]);

const ADVOCATE_STATUS_OPTIONS = [
  "CASE_CREATED",
  "DOCS_PENDING",
  "DOCS_VERIFIED",
  "ADVOCATE_ASSIGNED",
  "POA_GENERATED",
  "TEMP_BAIL_GRANTED",
  "DRIVER_BAIL_PETITION_FILED",
  "DRIVER_DOCS_VERIFIED_BY_POLICE",
  "IO_REPORT_UPLOADED",
  "DRIVER_ORDER_CREATED",
  "BAILER_DETAILS_ADDED",
  "DRIVER_BAIL_DONE",
  "VEHICLE_BAIL_PETITION_FILED",
  "VEHICLE_ORDER_CREATED",
  "VEHICLE_RELEASE_ORDER",
  "VEHICLE_BAIL_DONE",
  "VEHICLE_HANDOVER_DONE",
  "CASE_CLOSED",
];

const QS_STATUS_OPTIONS = [
  "NEW",
  "DETAILS_PENDING",
  "VEHICLE_SELECTED",
  "ASSIGNED",
  "MOVED",
  "REACHED_AT_PICKUP",
  "VEHICLE_INSPECTED",
  "OTP_VERIFIED",
  "WORKING",
  "DONE",
  "CANCELLED",
];

// Status options for all other (mock) tabs
const MOCK_STATUS_OPTIONS = [
  "New",
  "Assigned",
  "En route",
  "Reached",
  "Done",
  "Pending",
];

// Crane / recovery / legal: linked-to-accident-case vs standalone
const LINK_FILTER_OPTIONS = [
  { value: "", label: "All cases" },
  { value: "linked", label: "Linked Cases" },
  { value: "individual", label: "Individual" },
];

// Crane / recovery only: where the request was created
const SOURCE_FILTER_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "admin", label: "Admin" },
  { value: "whatsapp", label: "WhatsApp bot" },
];

// ─── Mock data (mechanic) ─────────────────────────────────────────────────────

const createMockServiceData = () => ({
  mechanic: [
    {
      id: "MEC-4001",
      customerName: "Ayesha Khan",
      phone: "9700012345",
      vehicleNo: "UP16GH7788",
      serviceLabel: "On-spot battery jumpstart",
      status: "Pending",
      timeAgo: "7 min ago",
      pickupLocation: "Noida sector 62",
      dropLocation: "On-site",
      documents: ["Vehicle photo"],
      vendors: [
        {
          id: "v11",
          name: "QuickFix Mechanic",
          sub: "2.1 km away",
          distance: "2.1 km",
          initials: "QF",
        },
        {
          id: "v12",
          name: "Auto Rescue Team",
          sub: "4.6 km away",
          distance: "4.6 km",
          initials: "AR",
        },
      ],
      assignedVendor: "",
      assignedVendorId: "",
      urgent: true,
    },
  ],
});

// Works out where a QS request came from. Adjust the field names / values here
// if your API uses something different.
function normalizeSource(r) {
  const s = String(
    r.source ?? r.createdFrom ?? r.createdVia ?? r.channel ?? r.createdBy ?? "",
  ).toLowerCase();
  if (s.includes("whatsapp") || s.includes("bot") || s.includes("chatbot")) {
    return "whatsapp";
  }
  if (s.includes("admin") || s.includes("dashboard") || s.includes("manual")) {
    return "admin";
  }
  return "";
}

function normalizeQSRequest(r) {
  const accidentCaseNo = r.accidentCaseNo || null;
  return {
    // identity
    id: r.requestCode,
    _id: r.id, // numeric DB id, used for API calls
    customerName: r.customerName || r.driverName || "—",
    customerContact: r.customerContact || "—",
    phone: r.driverContact || "—",
    vehicleNo: r.vehicleNumber || "—",
    serviceLabel: r.supportVehicleType
      ? VEHICLE_TYPE_LABEL[r.supportVehicleType] || r.supportVehicleType
      : r.serviceType === "crane"
        ? "Crane service"
        : "Recovery van",
    status: r.status,
    timeAgo: timeAgo(r.createdAt),
    pickupLocation: r.pickupLocation || "—",
    dropLocation: r.dropLocation || "—",
    pickupLat: r.pickupLat,
    pickupLng: r.pickupLng,
    dropLat: r.dropLat,
    dropLng: r.dropLng,
    urgent: r.urgent,
    // linked-case + source info (used by filters and the card)
    accidentCaseNo,
    isLinked: !!accidentCaseNo,
    source: normalizeSource(r), // "admin" | "whatsapp" | ""
    _raw: r,
    assignedVendor: r.vendorName || "",
    assignedVendorId: r.vendorId || "",
    documents: [],
    vendors: [],
  };
}

function normalizeAdvocateRequest(r) {
  return {
    id: r.caseNo,
    customerName: r.customerName || "—",
    phone: r.customerPhone || "—",
    vehicleNo: r.vehicleNumber || "—",
    serviceLabel: r.caseType || "Legal / accident case",
    status: r.caseStep,
    timeAgo: timeAgo(r.createdAt),
    pickupLocation: r.incidentLocation || r.authorityName || "—",
    dropLocation: "—",
    urgent: !!(r.deathReported || r.priority === "CRITICAL"),
    _raw: r,
    // linked accident case (used by the link filter and the card chip)
    accidentCaseNo: r.accidentCaseNo || null,
    isLinked: !!r.accidentCaseNo,
    assignedVendor: r.advocateName || "",
    assignedVendorId: r.advocateId || "",
    documents: [], // documents only come through on the detail endpoint
    vendors: [],
  };
}

const VEHICLE_TYPE_LABEL = {
  hydra_crane: "Hydra crane",
  recovery_non_flatbed: "Recovery van (non-flatbed)",
  recovery_flatbed: "Recovery van (flatbed)",
};

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

// ─── Page component ────────────────────────────────────────────────────────────

export default function QuickServicesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const globalQ = searchParams.get("q") || "";
  const { messages, setMessages, socketRef } = useWebSocket();
  const queryClient = useQueryClient();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(
    () => location.state?.activeTab ?? "crane",
  );
  const [highlightedCase, setHighlightedCase] = useState(
    () => location.state?.highlightedCase ?? null,
  );
  const highlightedCaseRef = useRef(null);
  const [localQ, setLocalQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [linkFilter, setLinkFilter] = useState("");     // "" | "linked" | "individual"
  const [sourceFilter, setSourceFilter] = useState(""); // "" | "admin" | "whatsapp"
  const [trackingRequest, setTrackingRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (!location.state?.highlightedCase) return;
    const timer = setTimeout(() => setHighlightedCase(null), 3000);
    return () => clearTimeout(timer);
  }, [location.state?.highlightedCase]);

  // ── Mock data (mechanic only, still unwired) ──────────────────────────────
  const [mockData, setMockData] = useState(() => createMockServiceData());

  // ── WebSocket: react to status changes and location updates ──────────────
  useEffect(() => {
    if (!messages.length) return;

    const queue = messages;
    for (const latest of queue) {
      if (!latest) continue;

      if (
        latest.type === "vehicle-tracking-update" ||
        latest.type === "join-ack"
      ) {
        continue;
      }

      // ── Location update: invalidate tracking query for the live map ──
      if (
        latest.message === "user-updated-location" &&
        latest.accidentVehicleCode
      ) {
        queryClient.setQueriesData({ queryKey: ["quick-services"] }, (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((r) =>
              r.request_code === latest.accidentVehicleCode
                ? {
                    ...r,
                    driver_lat: latest.lat,
                    driver_lng: latest.lng,
                    driver_last_ping: new Date().toISOString(),
                  }
                : r,
            ),
          };
        });
      }

      // ── Status change events: refetch the affected request + list ──
      const isQSStatusEvent =
        latest.forPage === "crane-user-all-cases" &&
        typeof latest.accidentVehicleCode === "string" &&
        latest.accidentVehicleCode.startsWith("QS-");

      if (isQSStatusEvent) {
        console.log("QS status broadcast received:", latest);
        // Refetch the specific card so its phase UI updates
        queryClient.invalidateQueries({
          queryKey: QS_KEYS.detail(latest.accidentVehicleCode),
        });
        // Refetch the whole list so status badge + stat cards update
        queryClient.invalidateQueries({ queryKey: ["quick-services"] });
        queryClient.invalidateQueries({ queryKey: QS_KEYS.stats() });
      }
    }

    // Consume so this effect doesn't re-run for the same message
    setMessages((prev) => prev.slice(queue.length));
  }, [messages]);

  const isCraneRecovery = activeTab === "crane" || activeTab === "recovery";
  const isLegal = activeTab === "advocate";

  const qsFilters = useMemo(() => {
    if (!isCraneRecovery) return null;
    const f = { service_type: activeTab };
    if (statusFilter) f.status = statusFilter;
    if (linkFilter) f.link_type = linkFilter;   // backend: "linked" | "individual"
    if (sourceFilter) f.source = sourceFilter;  // backend: "admin" | "whatsapp"
    if (localQ || globalQ) f.search = localQ || globalQ;
    return f;
  }, [isCraneRecovery, activeTab, statusFilter, linkFilter, sourceFilter, localQ, globalQ]);

  const {
    data: qsData,
    isLoading: qsLoading,
    isError: qsError,
    refetch: qsRefetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useQuickServiceRequests(qsFilters || {});

  const { data: allQsStats, refetch: refetchAllStats } =
    useQuickServiceStats("all");
  const { data: qsStats, refetch: refetchStats } = useQuickServiceStats(
    activeTab,
    { enabled: isCraneRecovery },
  );
  const { data: legalStats, refetch: refetchLegalStats } = useLegalCaseStats();

  const hasQuickFilters = !!(linkFilter || sourceFilter);

  const qsRequests = useMemo(() => {
    let list = (qsData?.pages ?? [])
      .flatMap((p) => p.data)
      .map(normalizeQSRequest);
    if (linkFilter === "linked") list = list.filter((r) => r.isLinked);
    if (linkFilter === "individual") list = list.filter((r) => !r.isLinked);
    if (sourceFilter) list = list.filter((r) => r.source === sourceFilter);
    return list;
  }, [qsData, linkFilter, sourceFilter]);

  const qsTotal = hasQuickFilters
    ? qsRequests.length
    : (qsData?.pages?.[0]?.total ?? qsRequests.length);

  const legalFilters = useMemo(() => {
    if (!isLegal) return {};
    const f = {};
    if (statusFilter) f.caseStep = statusFilter;
    if (linkFilter) f.link_type = linkFilter; // backend: "linked" | "individual"
    if (localQ || globalQ) f.search = localQ || globalQ;
    return f;
  }, [isLegal, statusFilter, linkFilter, localQ, globalQ]);

  const {
    data: legalData,
    isLoading: legalLoading,
    isError: legalError,
    refetch: legalRefetch,
    fetchNextPage: fetchNextLegalPage,
    hasNextPage: hasNextLegalPage,
    isFetchingNextPage: isFetchingNextLegalPage,
  } = useLegalCases(legalFilters, { enabled: true });

  // Client-side link filter as a safety net (no-op if the API already filters)
  const advocateRequests = useMemo(() => {
    let list = (legalData?.pages ?? [])
      .flatMap((p) => p.cases)
      .map(normalizeAdvocateRequest);
    if (linkFilter === "linked") list = list.filter((r) => r.isLinked);
    if (linkFilter === "individual") list = list.filter((r) => !r.isLinked);
    return list;
  }, [legalData, linkFilter]);
  const advocateTotal = linkFilter
    ? advocateRequests.length
    : (legalData?.pages?.[0]?.total ?? advocateRequests.length);

  // ── Infinite scroll sentinel — shared by crane/recovery AND legal ─────
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (isCraneRecovery && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        } else if (isLegal && hasNextLegalPage && !isFetchingNextLegalPage) {
          fetchNextLegalPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [
    isCraneRecovery,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    qsRequests.length,
    isLegal,
    hasNextLegalPage,
    isFetchingNextLegalPage,
    fetchNextLegalPage,
    advocateRequests.length,
  ]);

  // ── Derive tab counts ─────────────────────────────────────────────────────
  const tabCounts = useMemo(
    () => ({
      crane: allQsStats?.craneCount ?? 0,
      recovery: allQsStats?.recoveryCount ?? 0,
      advocate: legalStats?.allCases ?? advocateTotal,
      mechanic: mockData.mechanic.length,
    }),
    [allQsStats, legalStats, advocateTotal, mockData],
  );

  // ── Stats (mock-only until all tabs are wired) ────────────────────────────
  const stats = (isLegal ? legalStats : qsStats) || {};

  // ── Active list (mock or real) ────────────────────────────────────────────
  const isLoading = isCraneRecovery
    ? qsLoading
    : isLegal
      ? legalLoading
      : false;

  const isError = isCraneRecovery ? qsError : isLegal ? legalError : false;

  //  Mechanic: client-side filter on mock
  const filteredMock = useMemo(() => {
    if (activeTab !== "mechanic") return [];
    const raw = mockData.mechanic;
    const q = (localQ || globalQ).toLowerCase().trim();
    return raw.filter((r) => {
      const matchQ =
        !q ||
        r.id.toLowerCase().includes(q) ||
        r.customerName.toLowerCase().includes(q) ||
        r.vehicleNo.toLowerCase().includes(q);
      const matchStatus = !statusFilter || r.status === statusFilter;
      return matchQ && matchStatus;
    });
  }, [activeTab, mockData, localQ, globalQ, statusFilter]);

  const displayList = isCraneRecovery ? qsRequests : isLegal ? advocateRequests : filteredMock;

  useEffect(() => {
    if (!highlightedCase || !highlightedCaseRef.current) return;
    highlightedCaseRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [highlightedCase, displayList]);

  const displayCount = isCraneRecovery ? qsTotal : isLegal ? advocateTotal : filteredMock.length;
  // With link/source filters on and more pages still to load, the count is partial
  const countSuffix =
    (isCraneRecovery && hasQuickFilters && hasNextPage) ||
    (isLegal && linkFilter && hasNextLegalPage)
      ? "+"
      : "";
  const statusOptions = isCraneRecovery ? QS_STATUS_OPTIONS : isLegal ? ADVOCATE_STATUS_OPTIONS : MOCK_STATUS_OPTIONS;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleTabChange = (key) => {
    setActiveTab(key);
    setLocalQ("");
    setStatusFilter("");
    setLinkFilter("");
    setSourceFilter("");
  };

  const handleAssignVendor = (requestId, vendorId, serviceType) => {
    if (serviceType === "advocate" || isCraneRecovery) return;
    setMockData((prev) => ({
      ...prev,
      [serviceType]: (prev[serviceType] || []).map((r) => {
        if (r.id !== requestId) return r;
        const v = r.vendors.find((vn) => vn.id === vendorId);
        return {
          ...r,
          assignedVendor: v?.name || r.assignedVendor,
          assignedVendorId: vendorId,
          status: ["New", "Pending"].includes(r.status) ? "Assigned" : r.status,
        };
      }),
    }));
  };

  // Mock-tab status update
  const handleUpdateStatus = (requestId, newStatus, serviceType) => {
    if (serviceType === "advocate" || isCraneRecovery) return;
    setMockData((prev) => ({
      ...prev,
      [serviceType]: (prev[serviceType] || []).map((r) =>
        r.id === requestId ? { ...r, status: newStatus } : r,
      ),
    }));
  };

  const handleTrack = (request) => {
    if (!TRACKABLE_SERVICES.has(activeTab)) return;
    setTrackingRequest({ ...request, serviceType: activeTab });
  };

  const refetch = () => {
    if (isCraneRecovery) {
      qsRefetch();
      refetchAllStats();
      refetchStats();
    } else if (isLegal) {
      legalRefetch();
      refetchLegalStats();
    }
    else setMockData(createMockServiceData());
  };

  // ── Render ───────────────────

  return (
    <div className="qs-page">
      <div className="qs-page-header">
        <div>
          <h1 className="qs-page-title">Quick services</h1>
          <p className="qs-page-sub">
            On-demand service requests from chatbot &amp; customer app
          </p>
        </div>
        <div className="qs-header-actions">
          <div className="qs-live-badge">
            <span className="qs-live-dot" />
            Live
          </div>
          {/* "Create Request" */}
          {(isCraneRecovery || isLegal) && (
            <button
              className="wd-btn wd-btn-primary qs-create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon />
              Create Request
            </button>
          )}
          <button
            className="wd-btn wd-btn-secondary qs-refresh-btn"
            onClick={refetch}
            title="Refresh"
          >
            <RefreshIcon />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="qs-stats-row">
        <StatCard
          icon={<InboxIcon />}
          iconClass="qs-stat-icon--blue"
          value={stats.allCases}
          label="All cases"
          sub="Total requests"
        />
        <StatCard
          icon={<AlertIcon2 />}
          iconClass="qs-stat-icon--red"
          value={stats.awaitingAssignment}
          label="Awaiting assignment"
          sub="Needs action now"
          urgent
        />
        <StatCard
          icon={<TruckIcon />}
          iconClass="qs-stat-icon--blue"
          value={stats.inProgress}
          label="In progress"
          sub="Vendor dispatched"
        />
        <StatCard
          icon={<CheckIcon2 />}
          iconClass="qs-stat-icon--green"
          value={stats.completedToday}
          label="Completed"
          sub="All completed requests"
        />
      </div>

      {/* ── Tabs ── */}
      <div className="qs-tabs">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`qs-tab ${activeTab === key ? "qs-tab--active" : ""}`}
            onClick={() => handleTabChange(key)}
          >
            <Icon />
            {label}
            <span
              className={`qs-tab-count ${activeTab === key ? "qs-tab-count--active" : ""}`}
            >
              {tabCounts[key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="qs-filters">
        <div className="qs-search-wrap">
          <SearchIcon />
          <input
            className="qs-search"
            placeholder="Filter by name, vehicle, ID…"
            value={localQ}
            onChange={(e) => setLocalQ(e.target.value)}
          />
        </div>
        <select
          className="qs-status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {/* Linked / individual — crane, recovery and legal */}
        {(isCraneRecovery || isLegal) && (
          <select
            className="qs-status-select"
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value)}
            title="Linked to an accident case, or created individually"
          >
            {LINK_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        {/* Source (admin / WhatsApp bot) — crane and recovery only */}
        {isCraneRecovery && (
          <>
            <select
              className="qs-status-select"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              title="Where the request was created"
            >
              {SOURCE_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        )}

        <div className="qs-filter-meta">
          {displayCount}
          {countSuffix} request{displayCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* ── List ── */}
      <div className="qs-list">
        {isLoading && (
          <div className="qs-state-box">
            <div className="qs-spinner" />
            <span>Loading requests…</span>
          </div>
        )}

        {isError && (
          <div className="qs-state-box qs-state-box--error">
            <AlertIcon2 />
            <span>Failed to load requests.</span>
            <button className="wd-btn wd-btn-secondary" onClick={refetch}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && displayList.length === 0 && (
          <div className="qs-state-box">
            <InboxIcon />
            <span>No requests found</span>
            {(isCraneRecovery || isLegal) && !hasQuickFilters && (
              <button
                className="wd-btn wd-btn-primary"
                style={{ marginTop: 8 }}
                onClick={() => setShowCreateModal(true)}
              >
                <PlusIcon />
                Create first request
              </button>
            )}
          </div>
        )}

        {!isLoading &&
          !isError &&
          displayList.map((request, idx) => (
            <div
              key={request.id}
              className="qs-list-item"
              ref={
                request.id === highlightedCase ? highlightedCaseRef : null
              }
              style={
                request.id === highlightedCase
                  ? {
                      background: "rgba(59,130,246,.13)",
                      outline: "1px solid var(--wd-blue)",
                      borderRadius: 10,
                    }
                  : undefined
              }
            >
              <span className="qs-serial">{idx + 1}</span>
              <ServiceRequestCard
                request={request}
                serviceType={activeTab}
                onAssignVendor={handleAssignVendor}
                onUpdateStatus={handleUpdateStatus}
                onTrack={handleTrack}
                isAssigning={false}
                onNavigateVendors={
                  isLegal
                    ? () =>
                        navigate(
                          `/admin/vendors?requestCode=${request.id}&serviceType=advocate&tab=advocate`,
                        )
                    : isCraneRecovery
                      ? () =>
                          navigate(
                            `/admin/vendors?requestCode=${request.id}&serviceType=${activeTab}`,
                          )
                      : undefined
                }
              />
            </div>
          ))}

        {/* ── Infinite scroll sentinel (crane / recovery / legal) ── */}
        {(isCraneRecovery || isLegal) && !isLoading && !isError && (
          <>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {(isCraneRecovery
              ? isFetchingNextPage
              : isFetchingNextLegalPage) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 16,
                }}
              >
                <div className="qs-spinner" />
              </div>
            )}
            {isCraneRecovery && !hasNextPage && qsRequests.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: 12,
                  fontSize: 12,
                  color: "var(--wd-t3)",
                }}
              >
                All {qsTotal} requests loaded
              </div>
            )}
            {isLegal && !hasNextLegalPage && advocateRequests.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: 12,
                  fontSize: 12,
                  color: "var(--wd-t3)",
                }}
              >
                All {advocateTotal} requests loaded
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Live track modal ── */}
      {trackingRequest && (
        <div
          className="qs-track-modal-overlay"
          onClick={() => setTrackingRequest(null)}
        >
          <div
            className="qs-track-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <QSLiveTrackingPanel
              requestCode={
                trackingRequest._raw?.requestCode || trackingRequest.id
              }
              adminUserId={localStorage.getItem("userId")}
              wsRef={socketRef}
              pickup={
                trackingRequest._raw?.pickupLat &&
                trackingRequest._raw?.pickupLng
                  ? {
                      lat: Number(trackingRequest._raw.pickupLat),
                      lng: Number(trackingRequest._raw.pickupLng),
                      label: trackingRequest._raw.pickupLocation,
                    }
                  : null
              }
              dropoff={
                trackingRequest._raw?.dropLat && trackingRequest._raw?.dropLng
                  ? {
                      lat: Number(trackingRequest._raw.dropLat),
                      lng: Number(trackingRequest._raw.dropLng),
                      label: trackingRequest._raw.dropLocation,
                    }
                  : null
              }
              driverLocation={
                trackingRequest._raw?.driverLat &&
                trackingRequest._raw?.driverLng
                  ? {
                      lat: Number(trackingRequest._raw.driverLat),
                      lng: Number(trackingRequest._raw.driverLng),
                      lastPing: trackingRequest._raw.driverlastPing ?? null,
                    }
                  : null
              }
              onClose={() => setTrackingRequest(null)}
            />
          </div>
        </div>
      )}

      {/* ── Create modal — crane/recovery ── */}
      {showCreateModal && isCraneRecovery && (
        <CreatecraneServiceModal
          serviceType={activeTab}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            qsRefetch();
            refetchStats();
          }}
        />
      )}

      {/* ── Create Legal Case modal ── */}
      {showCreateModal && isLegal && (
        <CreateLegalCaseModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => {
            setShowCreateModal(false);
            const caseNo = created?.caseNo || created?.data?.caseNo;
            navigate(`/admin/quick-services/legal-cases/${caseNo}`, {
              state: { seedRow: created },
            });
          }}
        />
      )}
    </div>
  );
}

/* ── Stat card ── */
function StatCard({ icon, iconClass, value, label, sub, urgent }) {
  return (
    <div className={`qs-stat-card ${urgent ? "qs-stat-card--urgent" : ""}`}>
      <div className={`qs-stat-icon ${iconClass}`}>{icon}</div>
      <div className="qs-stat-value">{value}</div>
      <div className="qs-stat-label">{label}</div>
      {sub && <div className="qs-stat-sub">{sub}</div>}
    </div>
  );
}